import slugify from 'slugify';

/**
 * Model contentful items, stripping out any draft items
 * @param data The contentful field. Either an array or single object
 * @param Model The model to use to create the object
 */
export const modelWithoutDrafts = (
  data: Record<string, any> | Record<string, any>[],
  Model: any,
) => {
  // If the data is an array, map over it and create a new object
  if (Array.isArray(data)) {
    return data.map((item: Record<string, any>) => {
      if (!item.fields) {
        return {};
      }
      return new Model(item);
    }).filter((obj) => Object.keys(obj).length);
  }

  // If the data is not an array, create single object if has fields
  if (data.fields) {
    return new Model(data);
  }

  // Otherwise, return empty object
  return {};
};

export function extractVideoId(url: string) {
  // Regular expression pattern to match YouTube video URLs
  // eslint-disable-next-line max-len
  const pattern = /^(?:https?:\/\/)?(?:www\.)?(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?(?:.*&)?v=|v\/|e\/|\d{1,2}\/|user\/[^\/#?]*\/#?(?:.*[&?])?video_id=)|youtu\.be\/)([^&#?\/\n]*)/;

  // Get the video ID by matching the URL with the pattern
  const match = url.match(pattern);

  if (match && match[1]) {
    return match[1];
  }

  // Return null if no match found
  return undefined;
}

/**
 * Get the matching colour to go with a background colour.
 * This is added in the slider-products.edge via slider-products--text-{textColour}
 */
export const getTextColour = (bgCol: string) => {
  // const lightColours = ['white', 'light-sand', 'sand', 'light-grey'];
  const darkColours = ['navy', 'burgundy', 'black'];
  if (darkColours.includes(bgCol)) {
    return 'light';
  } else {
    return 'dark';
  }
};

export const formatDate = (rawDate, locale = 'en') => {
  const date = new Date(rawDate);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Toronto',
  };

  const localeToUse = LOCALE_MAP[locale] || 'en-CA';

  const formatter = new Intl.DateTimeFormat(localeToUse, options);

  return formatter.format(date);
};

/**
 * Format rolex watch slug using name and rmc
 * @param name The contentful field. Either an array or single object
 * @param rmc The model to use to create the object
 */

export function generateWatchSlug(name: string, rmc: string) {
  if (!name || !rmc) {
    return '';
  }

  return `${slugify(name).toLowerCase()}-${slugify(rmc).toUpperCase()}`;
}

/**
 * Extract Rolex RMC number from slug
 * @param slug The slug
 */

export const extractRMCNumber = (slug: string): string | null => {
  const pattern = /.*-(M[A-Za-z0-9]+-[A-Za-z0-9]+)$/;
  const match = slug.match(pattern);

  if (match && match[1]) {
    return match[1]; // Extract the captured group
  } else {
    return null; // Return null if there's no match
  }
};

const LOCALE_MAP = {
  'en': 'en-CA',
  'fr': 'fr-CA',
};

/**
 * Get the Contentful API locale name for a given Adonis locale.
 * Contentful will have en-CA for our main content, so we want "en" to map to that.
 * @param locale - The Adonis locale to format.
 * @param reverse - If we should reverse the mapping (i.e. Contentful to Adonis).
 * @returns The locale name for Contentful.
 */
export const formatLocaleForContentful = (locale: string, reverse = false) => {
  if (reverse) {
    return Object.keys(LOCALE_MAP).find((key) => LOCALE_MAP[key] === locale);
  }
  return LOCALE_MAP[locale] || 'en-CA';
};

/**
 * Cookiebot sets the CookieConsent cookie as a javascript object single quotes or none,
 * which is invalid JSON, so we need to fix it ourselves.
 * https://stackoverflow.com/questions/79309490/integrating-usercentrics-cookiebot-with-angular-18-and-ssr
 * @param cookie The cookie string to parse
 * @returns The parsed cookie consent object or null if not found
 */
export const parseCookiebotCookie = (cookie: string) => {
  if (!cookie) {
    return null;
  }

  const cleaned = cookie
    .replace(/'/g, '"') // Replace single quotes with double quotes
    .replace(/([a-zA-Z0-9_]+):/g, '"$1":'); // Add double quotes around property names
  const cookieConsentObj = JSON.parse(cleaned);
  return cookieConsentObj;
};

/**
 * Generates class names for colour/material selectors.
 * Class names will be {dial}-{colour} with any instances of dual colour in alphabetical order for easier CSS.
 * This only works with English colour names
 * @param name The colour/material name
 * @returns A class name string or undefined if no matches are found
 */
export function generateColourClasses(name: string) {
  // TODO: Is there a better way to handle this? We want to make sure that even if naming is inconsistent
  // in Shopify, that class names still apply correctly

  name = name.toLowerCase();

  let colourMatches;
  let dialsMatches;

  // colour options, these cannot be the same as dial options
  let colours = [
    'bronze',
    'brass',
    'yellow',
    'white',
    'rose',
    'platinum',
    'silver',
    'steel',
    'titanium',
    'dark case',
  ];

  // dial options
  let dials = ['dark', 'light', 'gold'];

  // build regex
  let colorsRe = new RegExp(colours.join('|'), 'gi');
  let dialsRe = new RegExp(dials.join('|'), 'gi');

  colourMatches = name.match(colorsRe);

  colourMatches = colourMatches ? colourMatches.sort() : undefined;

  // only do dials if we need to
  if (name.includes('dial')) {
    dialsMatches = name.match(dialsRe);
    dialsMatches = dialsMatches ? dialsMatches.sort() : undefined;
  }

  let classes: string[] = [];

  // check dials first, if dials we need to do both dials and colours

  let matches = dialsMatches ? dialsMatches.concat(colourMatches || []) : colourMatches;

  if (matches) {
    matches.forEach((match) => {
      classes.push(match.toLowerCase());
    });
  }

  return classes ? classes.join('-') : undefined;
};

/**
 * Format a list of items into a string
 * @param items Array of items to format
 * @param locale Locale to use for formatting
 * @returns Formatted string
 */
export function formatList(items: string[], locale: string): string {
  const formatter = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });
  return formatter.format(items);
};
